import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash, Pencil, FileText, ArrowsClockwise } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { usePagination } from '../components/TablePagination';
import SearchableSelect from '../components/SearchableSelect';
import { toDDMMYYYY, todayISO, addDaysISO } from '../lib/dateUtils';
import { loadCompanies } from '../lib/companies';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_COLOR = {
  draft: { bg: '#f3f4f6', color: '#374151' },
  sent: { bg: '#dbeafe', color: '#1e40af' },
  accepted: { bg: '#dcfce7', color: '#166534' },
  rejected: { bg: '#fee2e2', color: '#991b1b' },
  expired: { bg: '#fef9c3', color: '#854d0e' },
  converted: { bg: '#EEEDFE', color: '#3C3489' },
};

const emptyItem = () => ({ description: '', qty: 1, unit_price: 0, tax_percentage: 0, amount: 0, hsn_sac: '' });
const emptyQuote = () => ({
  id: '', quote_number: '', company_id: 'dextra_tech', client_id: '',
  quote_date: todayISO(), valid_until: addDaysISO(todayISO(), 14), status: 'draft',
  items: [emptyItem()], discount_value: 0, discount_type: 'amount',
  tax_percentage: 0, notes: '', terms: '', place_of_supply: '',
});

export const QuotesPage = () => {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(emptyQuote());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    try {
      const [q, c] = await Promise.all([
        axios.get(`${API_URL}/api/quotes`, { withCredentials: true }),
        axios.get(`${API_URL}/api/clients`, { withCredentials: true }),
      ]);
      setList(q.data); setClients(c.data);
      const cos = await loadCompanies();
      setCompanies(cos);
      if (cos.length && !cos.find((co) => co.id === form.company_id)) {
        setForm((f) => ({ ...f, company_id: cos[0].id }));
      }
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const companyById = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c])), [companies]);

  const filteredQuotes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((qq) => {
      if (statusFilter !== 'all' && qq.status !== statusFilter) return false;
      if (!q) return true;
      const cl = clientMap[qq.client_id];
      const co = companyById[qq.company_id];
      const blob = `${qq.quote_number || ''} ${co?.name || ''} ${co?.short || ''} ${cl?.first_name || ''} ${cl?.last_name || ''} ${cl?.company_name || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [list, search, statusFilter, clientMap, companyById]);
  const { paged: pagedQuotes, controls: quoteControls, startIdx: quoteStart } = usePagination(filteredQuotes, { storageKey: 'quotes-page-size', testIdPrefix: 'quote' });

  const setItem = (i, key, val) => setForm({ ...form, items: form.items.map((it, idx) => idx === i ? { ...it, [key]: val } : it) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { alert('Please select a client'); return; }
    const payload = {
      ...form,
      discount_value: parseFloat(form.discount_value) || 0,
      tax_percentage: parseFloat(form.tax_percentage) || 0,
      items: form.items.map((it) => ({
        description: it.description, qty: parseFloat(it.qty) || 0, unit_price: parseFloat(it.unit_price) || 0,
        tax_percentage: parseFloat(it.tax_percentage) || 0, hsn_sac: it.hsn_sac || '', amount: 0,
      })),
    };
    try {
      if (edit) await axios.put(`${API_URL}/api/quotes/${form.id}`, payload, { withCredentials: true });
      else await axios.post(`${API_URL}/api/quotes`, payload, { withCredentials: true });
      setOpen(false); setForm(emptyQuote()); setEdit(false); load();
    } catch (err) { alert('Save failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const onEdit = (q) => { setForm({ ...emptyQuote(), ...q, items: q.items?.length ? q.items : [emptyItem()] }); setEdit(true); setOpen(true); };
  const onDel = async (id) => {
    if (!window.confirm('Delete this quote?')) return;
    await axios.delete(`${API_URL}/api/quotes/${id}`, { withCredentials: true });
    load();
  };
  const onConvert = async (q) => {
    if (!window.confirm(`Convert quote ${q.quote_number} to an invoice?`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/quotes/${q.id}/convert`, {}, { withCredentials: true });
      alert(`Invoice ${r.data.invoice_number} created. You can find it under Invoices.`);
      load();
    } catch (err) { alert('Convert failed'); }
  };

  return (
    <div data-testid="quotes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--dx-text)' }}>Quotes</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>Send proposals; convert accepted quotes to invoices in one click</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyQuote()); setEdit(false); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-quote-btn" className="text-white" style={{ background: 'var(--dx-primary)' }}>
              <Plus size={18} className="mr-1" /> New Quote
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{edit ? 'Edit Quote' : 'New Quote'}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label>Company *</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{companies.map((co) => (<SelectItem key={co.id} value={co.id}>{co.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Client *</Label>
                  <SearchableSelect
                    options={clients.map((c) => {
                      const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—';
                      const company = (c.company_name || '').trim();
                      return {
                        value: c.id,
                        label: company ? `${name} — ${company}` : name,
                        sublabel: c.email || c.contact_no || '',
                        search: `${name} ${company} ${c.email || ''} ${c.contact_no || ''} ${c.gstin || ''}`,
                      };
                    })}
                    value={form.client_id}
                    onChange={(v) => setForm({ ...form, client_id: v })}
                    placeholder="Search client by name, company or email…"
                    testId="quote-client-select"
                  />
                </div>
                <div>
                  <Label>Quote No.</Label>
                  <Input value={form.quote_number} placeholder="auto" onChange={(e) => setForm({ ...form, quote_number: e.target.value })} />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['draft', 'sent', 'accepted', 'rejected', 'expired'].map((s) => (<SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Quote Date *</Label><Input required type="date" value={form.quote_date} onChange={(e) => setForm({ ...form, quote_date: e.target.value })} /></div>
                <div><Label>Valid Until *</Label><Input required type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
              </div>

              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm">Line Items</h3>
                  <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })}>
                    <Plus size={14} className="mr-1" /> Add row
                  </Button>
                </div>
                <table className="w-full text-sm">
                  <thead><tr style={{ background: 'var(--dx-bg)' }}>
                    <th className="text-left p-2 text-xs uppercase">Description</th>
                    <th className="text-right p-2 text-xs uppercase w-20">Qty</th>
                    <th className="text-right p-2 text-xs uppercase w-32">Unit Price</th>
                    <th className="text-right p-2 text-xs uppercase w-28">Tax %</th>
                    <th className="text-right p-2 text-xs uppercase w-32">Amount</th>
                    <th className="w-10"></th>
                  </tr></thead>
                  <tbody>
                    {form.items.map((it, i) => {
                      const amt = (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0);
                      return (
                        <tr key={i} className="border-b">
                          <td className="p-1"><Input value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} /></td>
                          <td className="p-1"><Input className="text-right" type="number" step="0.01" value={it.qty} onChange={(e) => setItem(i, 'qty', e.target.value)} /></td>
                          <td className="p-1"><Input className="text-right" type="number" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} /></td>
                          <td className="p-1"><Input className="text-right" type="number" step="0.01" value={it.tax_percentage} onChange={(e) => setItem(i, 'tax_percentage', e.target.value)} /></td>
                          <td className="p-2 text-right font-semibold">{fmt(amt)}</td>
                          <td className="p-1 text-center">
                            <Button type="button" size="sm" variant="outline" className="text-red-600" onClick={() => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })}>
                              <Trash size={14} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="save-quote-btn">{edit ? 'Update' : 'Create Quote'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="dx-card">
        <div className="p-3 flex items-center gap-3 flex-wrap border-b" style={{ borderColor: 'var(--dx-border)' }}>
          <Input placeholder="Search by quote #, client, company…" value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 max-w-md" data-testid="quote-search" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-sm px-2 py-2 border rounded bg-white" data-testid="quote-status-filter">
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="converted">Converted</option>
          </select>
        </div>
        <Table>
          <TableHeader><TableRow style={{ background: 'var(--dx-bg)' }}>
            <TableHead>#</TableHead>
            <TableHead>QUOTE #</TableHead><TableHead>COMPANY</TableHead><TableHead>CLIENT</TableHead>
            <TableHead>DATE</TableHead><TableHead>VALID UNTIL</TableHead>
            <TableHead className="text-right">TOTAL</TableHead><TableHead>STATUS</TableHead>
            <TableHead className="text-right">ACTIONS</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {pagedQuotes.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8" style={{ color: 'var(--dx-text2)' }}>
                <FileText size={32} className="mx-auto mb-2" style={{ color: 'var(--dx-text3)' }} />
                {list.length === 0 ? 'No quotes yet.' : 'No quotes match your filters.'}
              </TableCell></TableRow>
            ) : pagedQuotes.map((quote, idx) => {
              const cl = clientMap[quote.client_id];
              const co = companyById[quote.company_id];
              const col = STATUS_COLOR[quote.status] || STATUS_COLOR.draft;
              return (
                <TableRow key={quote.id} data-testid={`quote-row-${quote.id}`}>
                  <TableCell className="text-gray-400">{quoteStart + idx + 1}</TableCell>
                  <TableCell className="font-medium">{quote.quote_number}</TableCell>
                  <TableCell><span className="px-2 py-1 rounded text-[10px] font-bold uppercase" style={{ background: (co?.accent || '#1D9E75') + '22', color: co?.accent || '#1D9E75' }}>{co?.short}</span></TableCell>
                  <TableCell>{cl ? `${cl.first_name} ${cl.last_name || ''}` : '—'}</TableCell>
                  <TableCell>{toDDMMYYYY(quote.quote_date)}</TableCell>
                  <TableCell>{toDDMMYYYY(quote.valid_until)}</TableCell>
                  <TableCell className="text-right font-semibold">{fmt(quote.total)}</TableCell>
                  <TableCell><span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase" style={{ background: col.bg, color: col.color }}>{quote.status}</span></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {quote.status !== 'converted' && (
                        <Button size="sm" variant="outline" onClick={() => onConvert(quote)} title="Convert to invoice" data-testid={`convert-quote-${quote.id}`}>
                          <ArrowsClockwise size={14} />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => onEdit(quote)}><Pencil size={14} /></Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDel(quote.id)}><Trash size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {quoteControls}
      </Card>
    </div>
  );
};

export default QuotesPage;
